<template>
  <div class="home">
    <section ref="hero" class="hero">
      <video
        ref="video"
        class="hero-video"
        :src="video"
        :poster="poster"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
      ></video>
      <div class="hero-overlay"></div>
      <!-- Scroll position drives this wrapper's transform; the children keep their own load-in animation -->
      <div ref="heroContent" class="hero-content">
        <h1 class="hero-title rise-in">STUDIO BUTTER</h1>
        <a href="#services" class="btn hero-button rise-in">Take a look</a>
      </div>
    </section>

    <section id="services" class="services">
      <article class="service service--with-prompt">
        <img
          class="service-image service-image--tall"
          :src="selfTapesImage"
          alt="Four actors in self-tape close-ups against burgundy, green and teal backdrops"
          width="1200"
          height="2188"
        />
        <div v-reveal class="service-copy reveal">
          <h2 class="service-title">SELF-TAPES</h2>
          <p>
            Set your self-tape up for success! Our tapes are professionally shot, cinematically
            lit on our hand-painted backdrops and perfectly mic'd. Light coaching and direction
            is readily available if you need it. We take care of the set-up, the reader and the
            edit so you can simply show up and shine!
          </p>
         
          <router-link to="/booking#self-tapes" class="btn">Book a self-tape</router-link>
        </div>
        <!-- Quiet prompt to Headshots: vertical text + arrow in the right gutter on desktop, a chevron on mobile -->
        <a href="#headshots" class="next-prompt" aria-label="Book a headshot? Scroll to Headshots">
          <span class="next-prompt-label" aria-hidden="true">Book a headshot?</span>
          <svg class="next-prompt-arrow" viewBox="0 0 16 48" width="16" height="48" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">
            <path d="M8 0v46M1 39l7 7 7-7" />
          </svg>
          <svg class="next-prompt-chevron" viewBox="0 0 24 14" width="24" height="14" fill="none" stroke="currentColor" stroke-width="1.25" aria-hidden="true">
            <path d="M1 1l11 11L23 1" />
          </svg>
        </a>
      </article>

      <article id="headshots" class="service service--reverse">
        <img
          class="service-image"
          :src="headshotsImage"
          alt="Black and white film portrait of an actor leaning on his hand, eyes closed"
          width="1200"
          height="1810"
        />
        <div v-reveal class="service-copy reveal">
          <h2 class="service-title">HEADSHOTS</h2>
          <p>
            The artist's calling card. Stop everyone in their tracks with stunning photos of you
            on your best day. We shoot digital and film with packages for a range of needs.
            Retouching stays minimal but effective, plus you'll get your proofs within 48-hours
            of your session and final edits within 2-weeks.
          </p>
         
          <router-link to="/booking#headshots" class="btn">Book headshots</router-link>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import video from "@/assets/video/hero-reel.mp4";
import poster from "@/assets/video/hero-reel-poster.jpg";
import selfTapesImage from "@/assets/home/self-tapes-hero.jpg";
import headshotsImage from "@/assets/home/headshots.jpg";

export default {
  name: "HomePage",
  data() {
    return {
      video,
      poster,
      selfTapesImage,
      headshotsImage,
    };
  },
  methods: {
    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    },
    // As the page scrolls, lift the title and button off the top faster than the
    // page itself moves, and let the video drift slower for a little depth.
    updateHeroOnScroll() {
      this.scrollTicking = false;
      const hero = this.$refs.hero;
      if (!hero) return;
      const heroHeight = hero.offsetHeight;
      const progress = Math.min(Math.max(window.scrollY / heroHeight, 0), 1);

      this.$refs.heroContent.style.transform = `translate3d(0, ${-progress * heroHeight * 0.6}px, 0)`;
      this.$refs.heroContent.style.opacity = String(1 - progress * 1.3);
      this.$refs.video.style.transform = `translate3d(0, ${progress * heroHeight * 0.3}px, 0)`;
    },
    onScroll() {
      if (this.prefersReducedMotion() || this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(this.updateHeroOnScroll);
    },
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.updateHeroOnScroll();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
};
</script>

<style scoped>
/* Hero */
.hero {
  position: relative;
  height: calc(100vh - var(--nav-height));
  height: calc(100svh - var(--nav-height));
  background-color: var(--color-paper);
  overflow: hidden;
}

.hero-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: hero-fade-in 1.2s ease 0.4s both;
  will-change: transform;
}

/* Light wash over the video so the black title and button stay readable */
.hero-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(243, 239, 234, 0.4);
  animation: hero-fade-in 1.2s ease 0.4s both;
}

.hero-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 0 20px;
  text-align: center;
  will-change: transform, opacity;
}

.hero-title {
  font-family: "brown-sugar";
  font-size: 50px;
  color: var(--color-ink);
  animation-delay: 0.9s;
}

.hero-button {
  animation-delay: 1.1s;
}

@keyframes hero-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Rise up from below on page load */
.rise-in {
  animation-name: rise-in;
  animation-duration: 0.9s;
  animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
  animation-fill-mode: both;
}

@keyframes rise-in {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Services */
.services {
  /* Space above the first photo once "Take a look" lands here */
  --services-gap: 32px;
  scroll-margin-top: var(--nav-height);
  display: flex;
  flex-direction: column;
  gap: clamp(96px, 14vw, 180px);
  max-width: 1240px;
  margin: 0 auto;
  padding: var(--services-gap) 40px clamp(96px, 12vw, 160px);
}

.service {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  align-items: center;
  gap: clamp(40px, 7vw, 120px);
}

.service--reverse {
  grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
}

.service--reverse .service-image {
  order: 2;
}

/* Capped by viewport height; pinned to the outer edge so the two rows mirror */
.service-image {
  display: block;
  height: auto;
  object-fit: cover;
  aspect-ratio: 2 / 3;
  /* Height fits the window below the nav with --services-gap above and below */
  width: min(100%, calc((100vh - var(--nav-height) - var(--services-gap) * 2) * 2 / 3));
  max-width: 100%;
  justify-self: start;
}

.service--reverse .service-image {
  justify-self: end;
}

/* Stacked self-tape stills: keep the full image rather than cropping to 2:3 */
.service-image--tall {
  aspect-ratio: 1200 / 2188;
  width: min(100%, calc((100vh - var(--nav-height) - var(--services-gap) * 2) * 1200 / 2188));
}

.service-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  max-width: 46ch;
  font-size: 17px;
  line-height: 1.7;
}

.service--reverse .service-copy {
  justify-self: end;
}

.service-title {
  font-family: "brown-sugar";
  font-size: clamp(36px, 4vw, 50px);
  line-height: 1.1;
  margin-bottom: 4px;
}

/* Headshots sits mid-page, so leave the same gap above it as Self-Tapes gets */
#headshots {
  scroll-margin-top: calc(var(--nav-height) + var(--services-gap));
}

.service--with-prompt {
  position: relative;
}

/* Keep the copy clear of the prompt in the right gutter */
.service--with-prompt .service-copy {
  max-width: min(46ch, calc(100% - 56px));
}

.next-prompt {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: var(--color-ink);
  text-decoration: none;
  transition: color 0.3s ease;
}

.next-prompt:hover {
  color: var(--color-maroon);
}

.next-prompt-label {
  writing-mode: vertical-rl;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.next-prompt-arrow,
.next-prompt-chevron {
  display: block;
  animation: float 2.8s ease-in-out infinite;
}

.next-prompt-chevron {
  display: none;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(6px);
  }
}

@media (max-width: 899px) {
  .services {
    padding-left: 20px;
    padding-right: 20px;
  }

  .service,
  .service--reverse {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  /* Image always sits above its copy when stacked */
  .service--reverse .service-image {
    order: 0;
  }

  .service--reverse .service-copy {
    justify-self: start;
  }

  /* On mobile the prompt is just a soft chevron centred under the block */
  .next-prompt {
    position: static;
    justify-self: center;
    padding: 8px;
    margin-top: -8px;
    opacity: 0.45;
  }

  .next-prompt-label,
  .next-prompt-arrow {
    display: none;
  }

  .next-prompt-chevron {
    display: block;
  }

  .service--with-prompt .service-copy {
    max-width: 46ch;
  }

  .service-image,
  .service--reverse .service-image {
    width: min(100%, calc(75vh * 2 / 3));
    justify-self: center;
  }

  .service-image--tall {
    width: min(100%, calc(75vh * 1200 / 2188));
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-video,
  .hero-overlay,
  .rise-in,
  .next-prompt-arrow,
  .next-prompt-chevron {
    animation: none;
  }
}
</style>
